import { UserRoleEnumValue } from "@backend/src/schemas/enums";
import type { UserType } from "@/api/query/useCurrentUserQuery";
import { getCurrentOrgRole } from "./roleUtils";

type OrgRole = keyof typeof UserRoleEnumValue | null | undefined;

export type AssignedUsersLike = {
  user: { userId: string };
  projectRole: string | null;
}[];

export type NormalizedProjectScope = {
  portfolioportfolioId: string | null;
  programprogramId: string | null;
};

type ScopeInput =
  | {
      portfolioportfolioId?: string | null;
      programprogramId?: string | null;
      portfolio?: { portfolioId?: string } | null;
      program?: { programId?: string } | null;
    }
  | null
  | undefined;

export function normalizeProjectScope(input: ScopeInput): NormalizedProjectScope {
  if (!input) {
    return { portfolioportfolioId: null, programprogramId: null };
  }
  const pf =
    input.portfolioportfolioId ?? input.portfolio?.portfolioId ?? null;
  const pg = input.programprogramId ?? input.program?.programId ?? null;
  return { portfolioportfolioId: pf, programprogramId: pg };
}

function isOrgWideElevated(orgRole: OrgRole, user?: UserType | null): boolean {
  if (
    orgRole === UserRoleEnumValue.ADMINISTRATOR ||
    orgRole === UserRoleEnumValue.PROJECT_MANAGER
  ) {
    return true;
  }
  // Check across all user organisations
  return user?.userOrganisation?.some(
    (uo) =>
      uo.role === UserRoleEnumValue.ADMINISTRATOR ||
      uo.role === UserRoleEnumValue.PROJECT_MANAGER
  ) ?? false;
}

export function isOrgAdministrator(user: UserType | null | undefined, organisationId?: string): boolean {
  if (!user) return false;
  return getCurrentOrgRole(user, organisationId) === UserRoleEnumValue.ADMINISTRATOR;
}

export function canManageTimesheetApprovals(
  user: UserType | null | undefined,
): boolean {
  if (!user) return false;
  if (isOrgAdministrator(user)) return true;
  return (
    (user.managedPortfolioIds?.length ?? 0) > 0 ||
    (user.managedProgramIds?.length ?? 0) > 0 ||
    (user.managedProjectIds?.length ?? 0) > 0
  );
}

/** My Timesheet: submit clears line reviews; these users also auto-approve lines in their approval scope. */
export function canSubmitAndApproveOwnTimesheet(
  user: UserType | null | undefined,
): boolean {
  if (!user) return false;
  if (isOrgAdministrator(user)) return true;
  if (getCurrentOrgRole(user) === UserRoleEnumValue.PROJECT_MANAGER) {
    return true;
  }
  return (
    (user.managedPortfolioIds?.length ?? 0) > 0 ||
    (user.managedProgramIds?.length ?? 0) > 0 ||
    (user.managedProjectIds?.length ?? 0) > 0
  );
}

function projectRoleOnAssignment(
  assignedUsers: AssignedUsersLike | undefined,
  userId: string | undefined,
): string | null | undefined {
  if (!assignedUsers || !userId) return undefined;
  return assignedUsers.find((u) => u.user.userId === userId)?.projectRole;
}

export function isPortfolioOrProgramManagerForProjectScope(
  user: UserType | null | undefined,
  scope: NormalizedProjectScope,
): boolean {
  if (!user) return false;
  if (
    scope.portfolioportfolioId &&
    user.managedPortfolioIds?.includes(scope.portfolioportfolioId)
  ) {
    return true;
  }
  if (
    scope.programprogramId &&
    user.managedProgramIds?.includes(scope.programprogramId)
  ) {
    return true;
  }
  return false;
}

/**
 * UI gate aligned with backend task/project elevated access: org admin/PM,
 * project admin/PM on the team, or portfolio/program manager for this project’s hierarchy.
 */
export function canManageProjectTasksAndContent(
  user: UserType | null | undefined,
  orgRole: OrgRole,
  assignedUsers: AssignedUsersLike | undefined,
  projectInput: ScopeInput,
): boolean {
  if (!user) return false;
  if (isOrgWideElevated(orgRole, user)) return true;
  const scope = normalizeProjectScope(projectInput);
  if (isPortfolioOrProgramManagerForProjectScope(user, scope)) return true;
  const pr = projectRoleOnAssignment(assignedUsers, user.userId);
  return (
    pr === UserRoleEnumValue.ADMINISTRATOR ||
    pr === UserRoleEnumValue.PROJECT_MANAGER
  );
}

/**
 * Delete project: org administrator, portfolio/program manager for this project’s
 * hierarchy, or project-level ADMINISTRATOR only — not project-level PROJECT_MANAGER.
 */
export function canDeleteProject(
  user: UserType | null | undefined,
  orgRole: OrgRole,
  assignedUsers: AssignedUsersLike | undefined,
  projectInput: ScopeInput,
): boolean {
  if (!user) return false;
  if (orgRole === UserRoleEnumValue.ADMINISTRATOR) return true;
  const scope = normalizeProjectScope(projectInput);
  if (isPortfolioOrProgramManagerForProjectScope(user, scope)) return true;
  const pr = projectRoleOnAssignment(assignedUsers, user.userId);
  return pr === UserRoleEnumValue.ADMINISTRATOR;
}

export function canDuplicateOrRemoveProject(
  user: UserType | null | undefined,
  orgRole: OrgRole,
  _assignedUsers: AssignedUsersLike | undefined,
  projectInput: ScopeInput,
): boolean {
  if (!user) return false;
  if (orgRole === UserRoleEnumValue.ADMINISTRATOR) return true;
  const scope = normalizeProjectScope(projectInput);
  if (isPortfolioOrProgramManagerForProjectScope(user, scope)) return true;
  return false;
}

export function canManagePortfolioHierarchyRow(
  user: UserType | null | undefined,
  portfolioId: string,
): boolean {
  if (!user) return false;
  if (getCurrentOrgRole(user) === UserRoleEnumValue.ADMINISTRATOR) {
    return true;
  }
  return Boolean(user.managedPortfolioIds?.includes(portfolioId));
}

export function canManageProgramHierarchyRow(
  user: UserType | null | undefined,
  programId: string,
): boolean {
  if (!user) return false;
  if (getCurrentOrgRole(user) === UserRoleEnumValue.ADMINISTRATOR) {
    return true;
  }
  return Boolean(user.managedProgramIds?.includes(programId));
}

/**
 * Enhanced program management: allows portfolio managers to edit programs within their portfolio
 * and program managers to edit their assigned programs
 */
export function canManageProgramHierarchyRowEnhanced(
  user: UserType | null | undefined,
  programId: string,
  programPortfolioId: string | undefined,
): boolean {
  if (!user) return false;
  if (getCurrentOrgRole(user) === UserRoleEnumValue.ADMINISTRATOR) {
    return true;
  }
  
  // Program managers can edit their assigned programs
  if (user.managedProgramIds?.includes(programId)) {
    return true;
  }
  
  // Portfolio managers can edit programs within their portfolio
  if (programPortfolioId && user.managedPortfolioIds?.includes(programPortfolioId)) {
    return true;
  }
  
  return false;
}

export function canCreatePortfolioAndProgram(
  user: UserType | null | undefined,
  organisationId?: string,
): boolean {
  if (!user) return false;
  const role = getCurrentOrgRole(user, organisationId);
  return (
    role === UserRoleEnumValue.ADMINISTRATOR ||
    role === UserRoleEnumValue.PROJECT_MANAGER ||
    (user.managedPortfolioIds?.length ?? 0) > 0 ||
    (user.managedProgramIds?.length ?? 0) > 0
  );
}

export function resolveHighestProjectRoleBadge(
  orgRole: OrgRole,
  projectRole: string | null | undefined,
  isPortfolioManager: boolean = false,
  isProgramManager: boolean = false
): string {
  if (orgRole === UserRoleEnumValue.ADMINISTRATOR) {
    return "Admin";
  }
  if (isPortfolioManager) {
    return "PfM";
  }
  if (isProgramManager) {
    return "PgM";
  }
  if (projectRole === UserRoleEnumValue.PROJECT_MANAGER) {
    return "PM";
  }
  if (projectRole === UserRoleEnumValue.TEAM_MEMBER) {
    return "TM";
  }
  if (projectRole === UserRoleEnumValue.COLLABORATOR) {
    return "VI";
  }
  return "";
}
